import React, { useState, useEffect, useRef } from 'react';
import { countries } from './countries';
import { Search, ChevronDown, Globe } from 'lucide-react';
import './InternationalPhoneInput.css';

export default function InternationalPhoneInput({
  phone,
  onChangePhone,
  countryCode,
  onChangeCountryCode,
  placeholder = "Enter mobile number",
  required = false,
  id = "phone"
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handlePhoneInputChange = (e) => {
    // Sanitize: allow only numbers (0-9)
    const sanitized = e.target.value.replace(/[^0-9]/g, '');
    onChangePhone(sanitized);
  };

  const handlePhoneInputPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    // Sanitize pasted text: allow only numbers (0-9)
    const sanitized = pastedText.replace(/[^0-9]/g, '');
    onChangePhone(sanitized);
  };

  const handleSelectCountry = (country) => {
    onChangeCountryCode(country.dial);
    setIsOpen(false);
    setSearchQuery('');
  };

  const selectedCountry = countries.find(c => c.dial === countryCode) || { flag: '🌍', dial: countryCode };

  // Filter countries by name or dial code
  const filteredCountries = countries.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.dial.includes(searchQuery)
  );

  return (
    <div className="intl-phone-wrapper" ref={dropdownRef}>
      <div className="intl-phone-container">
        
        {/* Country Selector Button */}
        <button
          type="button"
          className="intl-country-selector-btn"
          onClick={() => setIsOpen(!isOpen)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label="Select Country Code"
        >
          <span className="intl-selected-flag" role="img" aria-label="Selected flag">
            {selectedCountry.flag}
          </span>
          <span className="intl-selected-dial">{selectedCountry.dial}</span>
          <ChevronDown size={14} className={`intl-chevron-icon ${isOpen ? 'open' : ''}`} />
        </button>

        {/* Divider */}
        <div className="intl-divider"></div>

        {/* Text Input */}
        <input
          type="tel"
          id={id}
          value={phone}
          onChange={handlePhoneInputChange}
          onPaste={handlePhoneInputPaste}
          placeholder={placeholder}
          required={required}
          className="intl-phone-text-input"
          autoComplete="tel-national"
        />
      </div>

      {/* Searchable Dropdown Overlay */}
      {isOpen && (
        <div className="intl-dropdown-menu" role="listbox">
          <div className="intl-search-container">
            <Search size={16} className="intl-search-icon" />
            <input
              type="text"
              ref={searchInputRef}
              placeholder="Search by country or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="intl-search-input"
              aria-label="Search country by name or code"
            />
          </div>

          <ul className="intl-country-list">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country, idx) => (
                <li key={`${country.code}-${idx}`} className="intl-country-item">
                  <button
                    type="button"
                    onClick={() => handleSelectCountry(country)}
                    className="intl-country-item-btn"
                    role="option"
                    aria-selected={countryCode === country.dial}
                  >
                    <span className="intl-flag-emoji" role="img" aria-label={country.name}>
                      {country.flag}
                    </span>
                    <span className="intl-country-name">{country.name}</span>
                    <span className="intl-country-dial">{country.dial}</span>
                  </button>
                </li>
              ))
            ) : (
              <li className="intl-no-results">No countries found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
